import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban, CalendarCheck, CheckCircle2, Copy, ExternalLink, FileText, Globe, History,
  Instagram, Linkedin, Mail, Pencil, Phone, Sparkles, UserRound, X,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtEuro } from "@/lib/constants";
import { PIPELINE, statusOf, actionLabel, ACTION_TYPES, frDate, frDateTime, sourceLabel } from "./constants";

const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const AVATAR_BG = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#FEF3C7] text-[#B45309]", "bg-[#EDE9FE] text-[#6D28D9]", "bg-[#DCFCE7] text-[#15803D]", "bg-[#FCE7F3] text-[#BE185D]"];
const avatarCls = (name = "") => AVATAR_BG[(name.charCodeAt(0) || 0) % AVATAR_BG.length];
const PRIO_CLS = { prioritaire: "bg-violet-50 text-violet-700 border-violet-200", haute: "bg-rose-50 text-rose-600 border-rose-200", moyenne: "bg-amber-50 text-amber-700 border-amber-200", basse: "bg-neutral-100 text-neutral-500 border-neutral-200" };
const PRIO_LABEL = { prioritaire: "Prioritaire", haute: "Haute", moyenne: "Moyenne", basse: "Basse" };
const CHANNELS = [["email", "Email", Mail], ["linkedin", "LinkedIn", Linkedin], ["instagram", "Instagram", Instagram]];

const ACT_ICON = { ia: Sparkles, statut: CheckCircle2, note: FileText, action: CalendarCheck, creation: Sparkles, conversion: CheckCircle2 };

export default function ProspectPanel({ prospect: p, onClose, onChanged, onEdit, onCreateRdv, onLost }) {
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [note, setNote] = useState("");
  const [aiBusy, setAiBusy] = useState("");
  const [draft, setDraft] = useState(null);
  const [prob, setProb] = useState(p.probability ?? "");
  const [na, setNa] = useState({ type: p.next_action?.type || "", date: p.next_action?.date || "" });

  useEffect(() => {
    api.get(`/prospects/${p.id}/activity`).then((r) => setActivity(r.data || [])).catch(() => {});
  }, [p.id, p.updated_at]); // eslint-disable-line

  const patch = async (fields, msgToast) => {
    try {
      await api.patch(`/prospects/${p.id}`, fields);
      if (msgToast) toast.success(msgToast);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const analyze = async () => {
    setAiBusy("analyze");
    try {
      await api.post(`/prospects/${p.id}/ai/analyze`);
      toast.success("Analyse IA terminée");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiBusy(""); }
  };
  const genDraft = async (channel, angle) => {
    setAiBusy(channel);
    try {
      const { data } = await api.post(`/prospects/${p.id}/ai/draft`, { channel, angle });
      setDraft(data);
      toast.success("Message préparé — ajouté à la file de validation de l'Agent IA");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiBusy(""); }
  };
  const createTaskFromAi = async () => {
    if (!ai?.prochaine_action) return;
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    patch({ next_action: { type: "autre", date: tomorrow, note: ai.prochaine_action.slice(0, 200) } },
      "Tâche créée dans la To Do (via prochaine action)");
  };
  const sequence = async () => {
    setAiBusy("sequence");
    try {
      await api.post(`/prospects/${p.id}/ai/sequence`, {});
      toast.success("Séquence créée — le 1er email est à valider dans l'Agent IA");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiBusy(""); }
  };
  const responseReceived = async () => {
    if (!window.confirm("Confirmer la réponse du prospect ? La séquence automatique sera stoppée immédiatement.")) return;
    try {
      const { data } = await api.post(`/prospects/${p.id}/ai/response-received`, {});
      toast.success(`Séquence stoppée — ${data.stopped} action(s) annulée(s)`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const optOut = async () => {
    const on = !p.do_not_contact;
    if (on && !window.confirm("Marquer « Ne plus contacter » ? Toutes les actions IA en cours seront annulées.")) return;
    try {
      await api.post(`/prospects/${p.id}/opt-out`, { on });
      toast.success(on ? "Prospect marqué « Ne plus contacter »" : "Prospect de nouveau contactable");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const convert = async () => {
    try {
      const { data } = await api.post(`/prospects/${p.id}/convert`);
      toast.success(data.existing ? "Fiche client existante liée" : "Client créé — historique conservé");
      onChanged();
      navigate(`/clients/${data.client_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/prospects/${p.id}/note`, { text: note.trim() });
      setNote("");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const copyDraft = () => {
    navigator.clipboard.writeText(draft ? `${draft.subject ? draft.subject + "\n\n" : ""}${draft.content}` : "");
    toast.success("Message copié");
  };

  const ai = p.ai;
  const st = statusOf(p.status);
  const value = p.value_kind === "recurrent" && p.value_monthly ? `${fmtEuro(p.value_monthly)} / mois` : p.value_amount ? fmtEuro(p.value_amount) : "—";

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-[480px] bg-white border-l border-neutral-200 shadow-2xl flex flex-col" data-testid="ppanel">
        {/* En-tête */}
        <div className="px-5 pt-4 pb-3 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-2xl text-xs font-bold flex items-center justify-center shrink-0 ${avatarCls(p.name)}`}>{initialsOf(p.name)}</span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-lg font-bold text-[#0F172A] truncate" data-testid="ppanel-name">{p.name}</h2>
              <p className="text-xs text-neutral-400 truncate">{[p.contact_name, p.fonction].filter(Boolean).join(" · ") || p.sector || "—"}</p>
            </div>
            <select value={p.status} onChange={(e) => {
              if (e.target.value === "perdu") { onLost(p); return; }
              patch({ status: e.target.value }, "Étape mise à jour");
            }} data-testid="ppanel-status"
              className={`text-[10px] font-semibold border rounded-full px-2.5 py-1 cursor-pointer focus:outline-none ${st.chip}`}>
              {PIPELINE.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button onClick={onClose} data-testid="ppanel-close" className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          {p.do_not_contact && (
            <p className="mt-2 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5" data-testid="ppanel-optout-badge">
              Ne plus contacter — toutes les actions IA sont bloquées.
            </p>
          )}
          {/* Actions rapides */}
          <div className="flex gap-1.5 mt-3" data-testid="ppanel-quick">
            {[
              ["Appeler", Phone, p.phone ? `tel:${p.phone}` : null, "ppanel-call"],
              ["Email", Mail, p.email ? `mailto:${p.email}` : null, "ppanel-email"],
            ].map(([label, Icon, href, tid]) => (
              <a key={tid} href={href || undefined} data-testid={tid}
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold border rounded-lg px-2.5 py-1.5 transition-colors ${href ? "border-neutral-200 text-neutral-600 hover:border-neutral-400" : "border-neutral-100 text-neutral-300 pointer-events-none"}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </a>
            ))}
            <button onClick={() => onCreateRdv(p)} data-testid="ppanel-rdv"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-neutral-200 rounded-lg px-2.5 py-1.5 text-neutral-600 hover:border-neutral-400 transition-colors">
              <CalendarCheck className="w-3.5 h-3.5" /> RDV
            </button>
            <button onClick={() => onEdit(p)} data-testid="ppanel-edit"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-neutral-200 rounded-lg px-2.5 py-1.5 text-neutral-600 hover:border-neutral-400 transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Modifier
            </button>
            {p.status === "gagne" && !p.converted_client_id && (
              <button onClick={convert} data-testid="ppanel-convert"
                className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> Convertir en client
              </button>
            )}
            {p.converted_client_id && (
              <button onClick={() => navigate(`/clients/${p.converted_client_id}`)} data-testid="ppanel-client-link"
                className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                Voir le client <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {/* Bloc Agent IA */}
          <section className="border border-violet-200 bg-violet-50/50 rounded-2xl p-3.5" data-testid="ppanel-ai">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <h3 className="text-xs font-bold text-[#0F172A]">Agent IA de prospection</h3>
              {ai && (
                <span className={`ml-auto text-[10px] font-bold border rounded-full px-2 py-0.5 ${PRIO_CLS[ai.priorite] || PRIO_CLS.moyenne}`} data-testid="ppanel-ai-score">
                  ✨ {ai.score}/100 · {PRIO_LABEL[ai.priorite] || ai.priorite}
                </span>
              )}
            </div>
            {ai ? (
              <div className="space-y-2 text-[11px] text-neutral-600 mb-2.5">
                {ai.sous_scores && (
                  <div className="grid grid-cols-5 gap-1" data-testid="ppanel-ai-subscores">
                    {[["Cible", ai.sous_scores.cible], ["Potentiel", ai.sous_scores.potentiel], ["Intention", ai.sous_scores.intention], ["Données", ai.sous_scores.donnees], ["Engagement", ai.sous_scores.engagement]].map(([l, v]) => (
                      <div key={l} className="text-center">
                        <div className="h-1 bg-violet-100 rounded-full overflow-hidden mb-0.5">
                          <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${v || 0}%` }} />
                        </div>
                        <p className="text-[8px] text-neutral-400 leading-tight">{l}</p>
                        <p className="text-[9px] font-bold text-violet-700 leading-tight">{v ?? "—"}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p data-testid="ppanel-ai-resume">{ai.resume}</p>
                {ai.raison && <p className="text-neutral-400 italic" data-testid="ppanel-ai-raison">{ai.raison}</p>}
                <p className="text-neutral-400">
                  Canal privilégié : <b className="text-neutral-600">{ai.canal_prefere}</b>
                  {ai.service_suggere && <> · Service : <b className="text-neutral-600">{ai.service_suggere}</b></>}
                  {ai.confiance && <> · Confiance IA : <b className="text-neutral-600">{{ elevee: "élevée", moyenne: "moyenne", faible: "faible" }[ai.confiance] || ai.confiance}</b></>}
                </p>
                {(ai.angles || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1" data-testid="ppanel-ai-angles">
                    <span className="text-[9px] text-neutral-400">Angles :</span>
                    {ai.angles.map((ang) => (
                      <button key={ang} onClick={() => genDraft("email", ang)} disabled={!!aiBusy || p.do_not_contact}
                        data-testid={`ppanel-angle-${ang.slice(0, 20)}`} title="Générer un email avec cet angle"
                        className="text-[9px] font-semibold border border-violet-200 text-violet-600 rounded-full px-2 py-0.5 hover:bg-violet-50 disabled:opacity-50 transition-colors">
                        {ang}
                      </button>
                    ))}
                  </div>
                )}
                {ai.prochaine_action && (
                  <p className="text-violet-700 font-semibold flex items-center gap-2 flex-wrap" data-testid="ppanel-ai-action">
                    → {ai.prochaine_action}
                    <button onClick={createTaskFromAi} data-testid="ppanel-ai-task"
                      className="text-[9px] font-semibold text-neutral-500 border border-neutral-200 rounded-full px-2 py-0.5 hover:border-neutral-400 transition-colors">
                      Créer la tâche
                    </button>
                  </p>
                )}
                {ai.profil_complet != null && (
                  <p className="text-[9px] text-neutral-400" data-testid="ppanel-ai-quality">
                    Profil complet : {ai.profil_complet} %{(ai.manquants || []).length > 0 ? ` — manque : ${ai.manquants.join(", ")}` : ""}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-neutral-400 mb-2.5">Aucune analyse pour l'instant — l'IA évalue l'adéquation, le canal et la prochaine action.</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              <button onClick={analyze} disabled={!!aiBusy || p.do_not_contact} data-testid="ppanel-analyze"
                className="inline-flex items-center gap-1 text-[10px] font-semibold bg-violet-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-violet-700 disabled:opacity-50 transition-colors">
                <Sparkles className="w-3 h-3" /> {aiBusy === "analyze" ? "Analyse…" : ai ? "Ré-analyser" : "Analyser"}
              </button>
              {CHANNELS.map(([id, label, Icon]) => (
                <button key={id} onClick={() => genDraft(id)} disabled={!!aiBusy || p.do_not_contact} data-testid={`ppanel-draft-${id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-white border border-violet-200 text-violet-700 rounded-lg px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-50 transition-colors">
                  <Icon className="w-3 h-3" /> {aiBusy === id ? "Rédaction…" : label}
                </button>
              ))}
              <button onClick={sequence} disabled={!!aiBusy || p.do_not_contact} data-testid="ppanel-sequence"
                className="inline-flex items-center gap-1 text-[10px] font-semibold bg-white border border-violet-200 text-violet-700 rounded-lg px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-50 transition-colors">
                {aiBusy === "sequence" ? "Création…" : "Créer une séquence"}
              </button>
            </div>
            {draft && (
              <div className="mt-2.5 bg-white border border-violet-200 rounded-xl p-3" data-testid="ppanel-draft">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-violet-500">Brouillon {draft.channel} — à valider dans l'Agent IA</span>
                  <button onClick={copyDraft} className="ml-auto text-neutral-400 hover:text-neutral-700" title="Copier" data-testid="ppanel-draft-copy">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                {draft.subject && <p className="text-[11px] font-bold text-neutral-700">Objet : {draft.subject}</p>}
                <p className="text-[11px] text-neutral-600 whitespace-pre-line leading-relaxed max-h-32 overflow-y-auto">{draft.content}</p>
              </div>
            )}
            <div className="flex gap-3 mt-2 pt-2 border-t border-violet-100">
              <button onClick={responseReceived} className="text-[10px] font-semibold text-emerald-700 hover:underline" data-testid="ppanel-response-received">
                Réponse reçue — stopper la séquence
              </button>
              <button onClick={optOut} className="text-[10px] font-semibold text-red-500 hover:underline" data-testid="ppanel-optout">
                {p.do_not_contact ? "Réactiver le contact" : "Ne plus contacter"}
              </button>
            </div>
          </section>

          {/* Opportunité */}
          <section className="grid grid-cols-2 gap-x-4 gap-y-2.5" data-testid="ppanel-opportunity">
            <div><p className="text-[10px] text-neutral-400">Service intéressé</p>
              <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{p.service || "—"}</p></div>
            <div><p className="text-[10px] text-neutral-400">Valeur estimée</p>
              <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{value}</p></div>
            <div><p className="text-[10px] text-neutral-400">Probabilité (%)</p>
              <input type="number" min="0" max="100" value={prob} data-testid="ppanel-probability"
                onChange={(e) => setProb(e.target.value)}
                onBlur={() => String(prob) !== String(p.probability ?? "") && patch({ probability: Number(prob) || null })}
                placeholder="—" className="mt-0.5 w-20 text-xs font-semibold text-[#0F172A] bg-transparent border-b border-transparent focus:border-neutral-300 focus:outline-none" /></div>
            <div><p className="text-[10px] text-neutral-400">Source</p>
              <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{sourceLabel(p.source)}</p></div>
            <div><p className="text-[10px] text-neutral-400">Responsable</p>
              <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{p.responsible || "—"}</p></div>
            <div><p className="text-[10px] text-neutral-400">Ajouté le</p>
              <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{frDate((p.created_at || "").slice(0, 10))}</p></div>
          </section>

          {/* Coordonnées */}
          <section className="space-y-1.5" data-testid="ppanel-infos">
            <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5 text-neutral-400" /> Informations</h3>
            {[["Email", p.email, p.email ? `mailto:${p.email}` : null, Mail],
              ["Téléphone", p.phone, p.phone ? `tel:${p.phone}` : null, Phone],
              ["Site web", p.site_web, p.site_web, Globe],
              ["LinkedIn", p.linkedin, p.linkedin, Linkedin],
              ["Instagram", p.instagram, p.instagram ? `https://instagram.com/${p.instagram.replace("@", "")}` : null, Instagram],
            ].map(([label, val, href, Icon]) => (
              <p key={label} className="flex items-center gap-2 text-xs">
                <Icon className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
                <span className="text-neutral-400 w-20 shrink-0">{label}</span>
                {val
                  ? <a href={href || undefined} target="_blank" rel="noreferrer" className={`font-medium truncate ${href ? "text-[#0369A1] hover:underline" : "text-neutral-700"}`}>{val}</a>
                  : <span className="text-neutral-300">—</span>}
              </p>
            ))}
            {(p.tags || []).length > 0 && (
              <p className="flex flex-wrap gap-1 pt-1">
                {(p.tags || []).map((t) => <span key={t} className="text-[9px] font-semibold bg-neutral-100 text-neutral-500 rounded-full px-2 py-0.5">#{t}</span>)}
              </p>
            )}
          </section>

          {/* Prochaine action */}
          <section data-testid="ppanel-next-action">
            <h3 className="text-xs font-bold text-[#0F172A] mb-1.5">Prochaine action</h3>
            <div className="flex gap-1.5">
              <select value={na.type} onChange={(e) => setNa({ ...na, type: e.target.value })} data-testid="ppanel-na-type"
                className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white">
                <option value="">Type…</option>
                {ACTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input type="date" value={na.date} onChange={(e) => setNa({ ...na, date: e.target.value })} data-testid="ppanel-na-date"
                className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white" />
              <Button size="sm" variant="outline" className="text-xs" disabled={!na.type || !na.date}
                onClick={() => patch({ next_action: { ...na, note: p.next_action?.note || "" } }, "Prochaine action planifiée — tâche To Do synchronisée")}
                data-testid="ppanel-na-save">OK</Button>
            </div>
          </section>

          {/* Historique multicanal */}
          <section data-testid="ppanel-history">
            <h3 className="text-xs font-bold text-[#0F172A] mb-2 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5 text-neutral-400" /> Historique
            </h3>
            <div className="space-y-2">
              {activity.map((a) => {
                const Icon = ACT_ICON[a.kind] || FileText;
                return (
                  <div key={a.id} className="flex items-start gap-2.5" data-testid={`ppanel-act-${a.id}`}>
                    <span className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${a.kind === "ia" ? "bg-violet-50 text-violet-500" : "bg-neutral-100 text-neutral-400"}`}>
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] text-neutral-600 leading-snug">{a.label}</p>
                      <p className="text-[9px] text-neutral-300">{frDateTime(a.at)}</p>
                    </div>
                  </div>
                );
              })}
              {activity.length === 0 && <p className="text-[11px] text-neutral-400">Aucun historique.</p>}
            </div>
            <div className="flex gap-1.5 mt-2.5">
              <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()}
                placeholder="Ajouter une note…" data-testid="ppanel-note-input"
                className="flex-1 text-xs border border-neutral-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-neutral-400" />
              <Button size="sm" variant="outline" className="text-xs" onClick={addNote} disabled={!note.trim()} data-testid="ppanel-note-add">OK</Button>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}
